import type { FC } from 'react';
import { useRef, useState } from 'react';

import { useNavigate } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { COMMUNITY_ROUTES } from '~/constants/community';
import useIsMobile from '~/hooks/useIsMobile';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { BottomSheetDialog } from '../ui/BottomSheetDialog';

import { useCommunitySearch, useCommunityTrendingPosts } from '../../_hooks/useCommunitySearch';
import { DesktopSearchModalContent } from './DesktopSearchModalContent';
import { MobileSearchSheet } from './MobileSearchSheet';

export const CommunitySearchModal: FC = () => {
  const { currentModal, handleCloseModal } = useGlobalModals();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isOpen = currentModal === 'search';

  const trendingPosts = useCommunityTrendingPosts({ enabled: isOpen });
  const { search, results, isSearching } = useCommunitySearch();

  const handleClose = () => {
    handleCloseModal({ preventScrollReset: true });
    setQuery('');
    search(''); // Cancel any pending debounced fetch
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    search(value);
  };

  const handleViewAll = (tab: string) => {
    handleCloseModal({ preventScrollReset: true });
    navigate(`${COMMUNITY_ROUTES.search}?q=${encodeURIComponent(query)}&tab=${tab}`);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim()) {
      handleCloseModal({ preventScrollReset: true });
      navigate(`${COMMUNITY_ROUTES.search}?q=${encodeURIComponent(query)}`);
      setQuery('');
    }
  };

  useNectarImpression(
    {
      pageBlock: NectarPageBlock.CommunitySearchModal,
    },
    {
      fireWhenInViewport: false,
      enabled: isOpen,
    },
  );

  if (isMobile) {
    return (
      <BottomSheetDialog
        isOpen={isOpen}
        onClose={handleClose}
        panelClassName="flex flex-col h-[100dvh] pt-[env(safe-area-inset-top)]"
      >
        <MobileSearchSheet
          query={query}
          onQueryChange={handleQueryChange}
          onClose={handleClose}
          onKeyDown={handleKeyDown}
          results={results}
          isSearching={isSearching}
          trendingPosts={trendingPosts}
          inputRef={inputRef}
        />
      </BottomSheetDialog>
    );
  }

  return (
    <DesktopSearchModalContent
      isOpen={isOpen}
      query={query}
      onQueryChange={handleQueryChange}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      onViewAll={handleViewAll}
      results={results}
      isSearching={isSearching}
      trendingPosts={trendingPosts}
      inputRef={inputRef}
    />
  );
};
